import {
  Button,
  CloseButton,
  Flex,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { useEffect, useState } from "react";
import {
  ExchangeCondition,
  directionOptions,
  referenceDateOptions,
} from "../../../models/ExchangeCondition";
import { saveExchangeCondition } from "../../../services/exchangeConditionService";

const CreateExchangeConditionModal = ({
  isOpen,
  onClose,
  onSaved,
  condition,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (id?: string) => void;
  condition?: ExchangeCondition;
}) => {
  const toast = useToast();
  const [name, setName] = useState("");
  const [referenceDate, setReferenceDate] = useState("shippingDate");
  const [direction, setDirection] = useState("Depois");
  const [days, setDays] = useState<string>("0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (isOpen) {
      setName(condition?.name ?? "");
      setReferenceDate(condition?.referenceDate ?? "shippingDate");
      setDirection(condition?.direction ?? "Depois");
      setDays(condition?.days != null ? String(condition.days) : "0");
      setError(undefined);
    }
  }, [isOpen, condition]);

  const handleSave = async () => {
    setError(undefined);
    if (name.trim().length === 0) {
      setError("Informe um nome para a condição.");
      return;
    }
    setLoading(true);
    const response = await saveExchangeCondition({
      id: condition?.id,
      name: name.trim(),
      referenceDate,
      direction,
      days: parseInt(days || "0", 10),
    });
    setLoading(false);

    if (response?.data?.success) {
      toast({
        title: condition?.id ? "Condição atualizada." : "Condição criada.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSaved?.(response.data.id);
      onClose();
    } else {
      toast({
        title: response?.data?.notifications?.[0] ?? "Erro ao salvar condição.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader>
          <Flex justifyContent="space-between" alignItems="center">
            <Heading size="md" fontWeight="semibold">
              {condition?.id ? "Editar condição" : "Nova condição de câmbio"}
            </Heading>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <FormLabel>Nome da condição:</FormLabel>
          <Input
            mb="16px"
            placeholder="Ex.: 50 dias após embarque"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <FormLabel>Data de referência do processo:</FormLabel>
          <Select
            mb="16px"
            value={referenceDate}
            onChange={(e) => setReferenceDate(e.target.value)}
          >
            {referenceDateOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>

          <Flex gap="16px">
            <div style={{ flex: 1 }}>
              <FormLabel>Quando:</FormLabel>
              <Select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              >
                {directionOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <FormLabel>Dias:</FormLabel>
              <Input
                type="number"
                min={0}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
          </Flex>

          <Text mt="16px" color="gray.600" fontSize="sm">
            A data do câmbio será preenchida {days || "0"} dia(s){" "}
            {direction.toLowerCase()} da data selecionada.
          </Text>

          {error && (
            <Text mt="12px" color={crm.danger}>
              {error}
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Flex w="100%" justifyContent="flex-end" gap="12px">
            <Button
              variant="outline"
              borderColor={crm.border}
              color={crm.brand}
              borderRadius="10px"
              _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              backgroundColor={crm.brand}
              color="white"
              borderRadius="10px"
              _hover={{ backgroundColor: crm.brandDark }}
              isLoading={loading}
              onClick={handleSave}
            >
              Salvar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateExchangeConditionModal;
